import React, { useState } from 'react';
import { Autocomplete, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormLabel, TextField, Tooltip, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

const CHIP_COLOR = '#1976d2';

const selectableChipSx = (selected, color = CHIP_COLOR) => ({
  bgcolor: selected ? color : 'transparent',
  color: selected ? '#fff' : color,
  border: `1px solid ${color}`,
  fontWeight: 500,
  cursor: 'pointer',
  '&:hover': { bgcolor: selected ? color : `${color}1a` },
});

const ChipSelect = ({ label, required, error, options, value, onSelect, emptyText = 'No options available' }) => (
  <Box>
    <FormLabel component="legend" required={required} error={error} sx={{ fontSize: '0.85rem', mb: 0.75, display: 'block' }}>
      {label}
    </FormLabel>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
      {options.length === 0 ? (
        <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>{emptyText}</Typography>
      ) : (
        options.map((opt) => (
          <Chip key={opt} label={opt} onClick={() => onSelect(opt)} sx={selectableChipSx(value === opt)} />
        ))
      )}
    </Box>
    {error && (
      <Typography variant="caption" sx={{ color: 'error.main', mt: 0.5, display: 'block' }}>{label} is required</Typography>
    )}
  </Box>
);

const YesNoChips = ({ label, required, error, value, onChange }) => (
  <Box>
    <FormLabel component="legend" required={required} error={error} sx={{ fontSize: '0.85rem', mb: 0.75, display: 'block' }}>
      {label}
    </FormLabel>
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Chip label="Yes" onClick={() => onChange(1)} sx={{ ...selectableChipSx(value === 1), flex: 1, justifyContent: 'center' }} />
      <Chip label="No" onClick={() => onChange(0)} sx={{ ...selectableChipSx(value === 0), flex: 1, justifyContent: 'center' }} />
    </Box>
  </Box>
);

const OtherDetailsCard = ({
  formVals,
  status,
  empList,
  customerType,
  edit,
  formChangeHandler,
  getFieldLabel,
  isFieldRequired,
  isFieldVisible,
  customerContacts,
  privatePayerSupportSetting,
  user,
  errors = {},
}) => {
  const primaryBusinessType = user?.comp_settings?.find((s) => s.feature_value === 'primaryBusinessType')?.feature_id || null;
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');

  const pManager = !empList ? [] : empList.map((emp) => ({
    value: emp.id || emp.emp_id,
    title: emp.employee_name ? emp.employee_name : emp.emp_first_name + ' ' + emp.emp_last_name,
  }));

  const cusContactList = !customerContacts ? [] : customerContacts.map((contact) => ({
    value: contact.con_id,
    title: contact.con_first_name + ' ' + contact.con_last_name,
  }));

  const handleFieldChange = (field) => (e) => formChangeHandler({ target: { id: field, value: e.target?.value } });

  const handleClose = () => {
    formChangeHandler({ target: { id: 'status', value: 'Active' } });
    setOpen(false);
  };
  const handleDialogSubmit = () => {
    formChangeHandler({ target: { id: 'status', value: 'Dormant' } });
    setOpen(false);
  };

  const dateFields = ['sign_page_date', 'last_plan_approval_date', 'last_assess_date', 'renewal_date', 'MedicaidEndDate', 'AgencyAssignDate'];
  const textFields = ['DDD_tier', 'diagnose_code', 'waiver_program', 'County', 'Race', 'Ethnicity', 'TransferOutReason'];
  const njcatFields = ['NJCATSelfcare', 'NJCATBehavioral', 'NJCATMedical', 'NJCATFCA'];
  const placeCharFields = ['place01char', 'place02char', 'place03char', 'place04char', 'place05char', 'place06char', 'place07char', 'place08char'];
  const placeDateFields = ['place09date', 'place10date', 'place11date', 'place12date'];
  const placeNumberFields = ['place13number', 'place14number', 'place15number', 'place16number'];

  // Each field renders as a half-width cell so fields flow two-per-row, matching the original Grid (size=6) layout.
  const cell = (key, content, tip) => {
    const box = <Box key={tip ? undefined : key} sx={{ flex: '1 1 45%', minWidth: 200 }}>{content}</Box>;
    return tip ? <Tooltip key={key} title={tip} placement="top">{box}</Tooltip> : box;
  };

  const renderDate = (field, label) => (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        label={label}
        value={formVals[field] ? dayjs(typeof formVals[field] === 'string' ? formVals[field].replace(/[TZ].*$/, '') : formVals[field]) : null}
        onChange={(d) => { if (d && d.isValid()) formChangeHandler({ target: { id: field, value: d.format('YYYY-MM-DD') } }); }}
        slotProps={{ textField: { id: field, size: 'small', required: isFieldRequired(field), fullWidth: true, error: !!errors[field] } }}
      />
    </LocalizationProvider>
  );

  const textCell = (field, opts = {}) => isFieldVisible(field) ? cell(field, (
    <TextField
      fullWidth
      size="small"
      variant="outlined"
      type={opts.type || 'text'}
      inputProps={opts.type === 'number' ? { inputMode: 'numeric', pattern: '[0-9]*' } : (opts.maxLength ? { maxLength: opts.maxLength } : undefined)}
      label={getFieldLabel(field, field)}
      id={field}
      value={formVals[field] || ''}
      onChange={handleFieldChange(field)}
      required={isFieldRequired(field)}
      error={!!errors[field]}
    />
  ), opts.tip) : null;

  const dateCell = (field, tip) => isFieldVisible(field) ? cell(field, renderDate(field, getFieldLabel(field, field)), tip) : null;

  return (
    <>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 1.5 }}>
        {dateCell('acquired_date')}

        {cell('medicaid_id', (
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            id="medicaid_id"
            label="Medicaid ID"
            value={formVals.medicaid_id || ''}
            onChange={(e) => formChangeHandler({ target: { id: 'medicaid_id', value: e.target.value || '' } })}
          />
        ))}

        {isFieldVisible('status') && (
          <Box key="status" sx={{ flex: '1 1 100%' }}>
            <ChipSelect
              label="Status"
              required
              error={!!errors.status}
              options={status || []}
              value={formVals.status || ''}
              onSelect={(val) => {
                if (val === 'Dormant' && !formVals.TransferOutReason) setOpen(true);
                formChangeHandler({ target: { id: 'status', value: val } });
              }}
            />
          </Box>
        )}

        {isFieldVisible('customer_type') && edit && (
          <Box key="customer_type" sx={{ flex: '1 1 100%' }}>
            <ChipSelect
              label={getFieldLabel('customer_type', 'Customer Type')}
              required={isFieldRequired('customer_type')}
              error={!!errors.customer_type}
              options={customerType || []}
              value={formVals.customer_type || ''}
              onSelect={(val) => formChangeHandler({ target: { id: 'customer_type', value: val } })}
            />
          </Box>
        )}

        {isFieldVisible('pManager') && cell('pManager', (
          <Autocomplete
            fullWidth
            size="small"
            options={pManager}
            getOptionLabel={(option) => option.title}
            value={pManager.find((pm) => pm.value === formVals.pManager) || null}
            onChange={(e, newVal) => formChangeHandler({ target: { id: 'pManager', value: newVal ? newVal.value : '' } })}
            renderInput={(params) => (
              <TextField {...params} label={getFieldLabel('pManager', 'Project Manager')} variant="outlined" required={isFieldRequired('pManager')} error={!!errors.pManager} />
            )}
          />
        ))}

        {isFieldVisible('private_payer_responsible') && privatePayerSupportSetting && cell('private_payer_responsible', (
          <Autocomplete
            fullWidth
            size="small"
            options={[{ value: 'Self', title: 'Self' }, ...cusContactList]}
            getOptionLabel={(option) => option.title}
            value={formVals.private_payer_responsible
              ? cusContactList.find((p) => p.value === formVals.private_payer_responsible) || { value: 'Self', title: 'Self' }
              : { value: 'Self', title: 'Self' }}
            onChange={(e, newVal) => formChangeHandler({ target: { id: 'private_payer_responsible', value: newVal ? newVal.value : 'Self' } })}
            renderInput={(params) => (
              <TextField {...params} label={getFieldLabel('private_payer_responsible', 'Private Payer Responsible')} variant="outlined" required={isFieldRequired('private_payer_responsible')} error={!!errors.private_payer_responsible} />
            )}
          />
        ))}

        {dateFields.map((field) => dateCell(field))}

        {textFields.map((field) => textCell(field))}

        {isFieldVisible('func_criteria_met') && cell('func_criteria_met', (
          <YesNoChips
            label={getFieldLabel('func_criteria_met', 'Functional Criteria Met')}
            required={isFieldRequired('func_criteria_met')}
            error={!!errors.func_criteria_met}
            value={formVals.func_criteria_met === 1 ? 1 : formVals.func_criteria_met === 0 ? 0 : null}
            onChange={(v) => formChangeHandler({ target: { id: 'func_criteria_met', value: v } })}
          />
        ))}

        {njcatFields.map((field) => textCell(field, { type: 'number' }))}

        {placeCharFields.map((field) => textCell(field, { maxLength: 255, tip: getFieldLabel(field, field) }))}

        {placeDateFields.map((field) => dateCell(field, getFieldLabel(field, field)))}

        {placeNumberFields.map((field) => textCell(field, { type: 'number', tip: getFieldLabel(field, field) }))}

        {primaryBusinessType === 103 && (
          <>
            {cell('HasPet', (
              <YesNoChips
                label="Has Pet"
                error={!!errors.HasPet}
                value={formVals.HasPet === 1 ? 1 : formVals.HasPet === 0 ? 0 : null}
                onChange={(v) => formChangeHandler({ target: { id: 'HasPet', value: v } })}
              />
            ))}
            {cell('ProviderGenderPreference', (
              <ChipSelect
                label="Provider Gender Preference"
                error={!!errors.ProviderGenderPreference}
                options={['Male', 'Female', 'No Preference']}
                value={formVals.ProviderGenderPreference || ''}
                onSelect={(val) => formChangeHandler({ target: { id: 'ProviderGenderPreference', value: val } })}
              />
            ))}
            {cell('LanguagePreference', (
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label="Language Preference"
                id="LanguagePreference"
                value={formVals.LanguagePreference || ''}
                onChange={handleFieldChange('LanguagePreference')}
                inputProps={{ maxLength: 50 }}
                error={!!errors.LanguagePreference}
              />
            ))}
            {cell('PreferRepeatProviders', (
              <YesNoChips
                label="Prefer Repeat Providers"
                error={!!errors.PreferRepeatProviders}
                value={formVals.PreferRepeatProviders === 1 ? 1 : formVals.PreferRepeatProviders === 0 ? 0 : null}
                onChange={(v) => formChangeHandler({ target: { id: 'PreferRepeatProviders', value: v } })}
              />
            ))}
            {cell('DriverLicenseNumber', (
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label="Driver License Number"
                id="DriverLicenseNumber"
                value={formVals.DriverLicenseNumber || ''}
                onChange={handleFieldChange('DriverLicenseNumber')}
                inputProps={{ maxLength: 50 }}
                error={!!errors.DriverLicenseNumber}
              />
            ))}
            {cell('DLIssueDate', renderDate('DLIssueDate', 'DL Issue Date'))}
            {cell('DLExpiryDate', renderDate('DLExpiryDate', 'DL Expiry Date'))}
            {cell('DLIssueState', (
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label="DL Issue State"
                id="DLIssueState"
                value={formVals.DLIssueState || ''}
                onChange={handleFieldChange('DLIssueState')}
                inputProps={{ maxLength: 2 }}
                placeholder="e.g., NJ"
                error={!!errors.DLIssueState}
              />
            ))}
          </>
        )}
      </Box>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Please enter a reason &amp; date below</DialogTitle>
        <DialogContent>
          <Autocomplete
            id="TransferOutReason"
            options={['Individual Passed Away', 'Switched Providers', 'Ended Service', 'Returned to DDD Case Support', 'Moved to MLTSS', 'Moved to another state', 'Disenrolled from DDD', 'Disapproved']}
            fullWidth
            value={reason}
            onChange={(e, newVal) => {
              setReason(newVal);
              formChangeHandler({ target: { id: 'TransferOutReason', value: newVal } });
            }}
            renderInput={(params) => <TextField {...params} label="Reason *" variant="standard" fullWidth />}
          />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Date *"
              value={formVals.TransferOutDate ? dayjs(formVals.TransferOutDate.replace(/T.*$/, '')) : null}
              onChange={(d) => { if (d && d.isValid()) formChangeHandler({ target: { id: 'TransferOutDate', value: d.format('YYYY-MM-DD') } }); }}
              slotProps={{ textField: { id: 'TransferOutDate', fullWidth: true } }}
            />
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleDialogSubmit} disabled={!reason || !formVals.TransferOutDate}>Submit</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OtherDetailsCard;
